<template>
  <v-container class="pt-12 px-5 px-md-7">
    <v-row class="justify-lg-space-between align-center">
      <v-col class="pa-0">
        <v-card-title class="py-0 title font-weight-bold letter-spacing-normal">
          Sightings
        </v-card-title>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="py-3 mb-5">
        <div id="speciesSearch" class="position-relative">
          <v-autocomplete
            ref="autocomplete"
            v-model="fakeModel"
            :search-input.sync="speciesFilter"
            @input="selectSpecies"
            :items="speciesList"
            :multiple="false"
            attach="#speciesSearch"
            item-text="name"
            return-object
            :filter="() => true"
            label="Search"
            append-icon="$search"
            solo
            class="font-weight-bold"
            color="black"
          />
        </div>
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col class="py-3">
        <v-card class="px-3 py-3">
          <v-skeleton-loader
            v-if="!isEditMode && !speciesModel.fetched"
            type="list-item-two-line"
          />
          <v-list v-else dense max-height="400" class="overflow-auto">
            <v-list-item v-if="!attendeeSpecies.length" class="d-flex">
              Nothing to show here!
            </v-list-item>
            <v-list-item
              class="d-flex"
              v-for="(species, index) in attendeeSpecies"
              :key="index"
            >
              <v-list-item-avatar size="80" class="ma-0" tile>
                <v-img
                  :src="getMedia(species)"
                  lazy-src="/placeholder.jpg"
                  aspect-ratio="1"
                  class="grey lighten-2"
                />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title
                  class="ml-5 title font-weight-bold letter-spacing-normal"
                >
                  {{ species.name }}
                  <v-spacer />
                </v-list-item-title>
              </v-list-item-content>
              <div class="price-table">
                <v-list-item-action
                  class="font-weight-bold letter-spacing-normal align-center"
                >
                  {{ request.getGlobalPrice(member.id, species.id) }}
                </v-list-item-action>
              </div>
              <v-list-item-action>
                {{ request.getCount(member.id, species.id) }}
              </v-list-item-action>
              <div class="d-flex elevation-3 ml-2">
                <v-list-item-action
                  class="ma-0 d-flex  white black--text text-center justify-center pa-2 v-card--link"
                  @click="reduceSighting(species)"
                >
                  <v-icon
                    title="Reduce Sighting"
                    class="white black--text caption ma-0"
                    size="20"
                  >
                    $minus
                  </v-icon>
                </v-list-item-action>
                <v-divider :vertical="true" />
                <v-list-item-action
                  class="ma-0 d-flex  white black--text text-center justify-center pa-2 v-card--link"
                  @click="increaseSighting(species)"
                >
                  <v-icon
                    title="Increase Sighting"
                    class="white black--text caption ma-0"
                    size="20"
                  >
                    $plus
                  </v-icon>
                </v-list-item-action>
              </div>
            </v-list-item>
          </v-list>
          <v-list v-if="request.getTotalPrice()">
            <v-list-item-action
              class="font-weight-bold letter-spacing-normal ma-0 d-flex  white black--text text-right justify-center pa-2 v-card--link"
            >
              <div>
                {{ 'Total Contribution Amount ' + ' $' }}
                <input
                  v-model="totalAmount"
                  type="number"
                  id="totalAmountInput"
                />
              </div>
            </v-list-item-action>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import SpeciesModel from '@models/Species';
import GroupSightingsRequest from '@request/GroupSightingsRequest';
import CustomSightingsRequest from '@request/CustomSightingsRequest';

export default {
  name: 'CustomTripSightings',
  props: {
    member: {
      type: Object,
      required: true,
    },
    request: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    title: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      speciesModel: new SpeciesModel({
        itemsPerPage: 5,
      }),
      fakeModel: {},
      speciesFilter: '',
      frequentSpecies: [],
      speciesList: [],
      species: [],
      disableSpeciesFilter: false,
    };
  },
  computed: {
    attendeeSpecies() {
      let species = [];
      let self = this;
      species = self.frequentSpecies.concat(
        self.species.filter(
          (s) => !self.frequentSpecies.find((sp) => sp.id === s.id)
        )
      );
      return species;
    },
    totalAmount: {
      get() {
        return this.request.getTotalPrice();
      },
      set(value) {
        this.request.amount = value;
      },
    },
  },
  watch: {
    'member.id': {
      handler() {
        this.species = [];
        if (this.speciesFilter) {
          this.speciesFilter = '';
        }
        this.syncSpeciesWithRequest();
      },
      deep: true,
    },
    speciesFilter() {
      if (this.disableSpeciesFilter) {
        this.disableSpeciesFilter = false;
        return;
      }
      if (!this.speciesModel.fetched) {
        return;
      }
      this.fetchSpecies();
    },
    getTotalPrices() {
      this.totalAmount = this.request.getTotalPrice();
    },
  },
  methods: {
    getTitle() {
      let name = this.member.first_name;
      if (name.endsWith('s')) {
        return `${name}' Sightings`;
      }
      return `${name}'s Sightings`;
    },
    async fetchSpecies() {
      this.speciesModel.setFilters({
        name: this.speciesFilter,
        excludes: this.attendeeSpecies
          .reduce((ids, species) => ids + ' ' + species.id, '')
          .trim()
          .replace(/\s/g, ', '),
      });
      let response = await this.speciesModel.get();
      if (response) {
        this.speciesList = response.data;
      }
    },
    async selectSpecies(species) {
      if (!species) {
        return;
      }
      this.disableSpeciesFilter = true;
      this.fakeModel = {};
      await this.$nextTick();
      if (this.attendeeSpecies.find((s) => s.id === species.id)) {
        $showInfo('The species has already been added!');
        return;
      }
      this.species.push(species);
      await this.$nextTick();
      this.fetchSpecies();
    },
    increaseSighting(species) {
      this.request.changeSightingsData(this.member, species);
    },
    reduceSighting(species) {
      this.request.changeSightingsData(this.member, species, -1);
    },
    async getFrequentSpecies() {
      if (this.isEditMode) {
        return [];
      }
      let model = new SpeciesModel({
        itemsPerPage: 4,
        filters: {
          tour_id: this.request.tour_id || null,
        },
        sortBy: ['frequent'],
        sortDesc: [true],
      });
      let response = await model.get();
      if (response) {
        this.frequentSpecies = response.data;
      }
      await this.$nextTick();
      this.fetchSpecies();
    },
    syncSpeciesWithRequest() {
      let self = this;
      let attendees = self.request.attendees.filter(
        (attendee) =>
          attendee.member_id === self.member.id &&
          !self.attendeeSpecies.find(
            (species) => species.id === attendee.species.id
          )
      );
      if (attendees.length) {
        let species = attendees.map((data) => data.species);
        self.species = self.species.concat(species);
      }
      this.fetchSpecies();
    },
  },
  mounted() {
    this.getFrequentSpecies();
    this.syncSpeciesWithRequest();
  },
};
</script>

<style scoped>
#totalAmountInput {
  border-style: solid;
  border-color: #c4c4c4;
}
</style>
